import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

const VIEWER_SIZE = 512;

interface ModelViewer {
  canvas: HTMLCanvasElement;
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  group: THREE.Group;
  loaded: boolean;
}

const viewers = new Map<string, ModelViewer>();
let loopStarted = false;

function startLoop() {
  if (loopStarted) return;
  loopStarted = true;
  const tick = () => {
    for (const viewer of viewers.values()) {
      if (!viewer.loaded) continue;
      viewer.group.rotation.y += 0.008;
      viewer.renderer.render(viewer.scene, viewer.camera);
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

export function getModelViewerCanvas(assetId: string): HTMLCanvasElement | null {
  return viewers.get(assetId)?.canvas ?? null;
}

export function releaseModelViewer(assetId: string): void {
  const viewer = viewers.get(assetId);
  if (!viewer) return;
  viewer.group.traverse((object) => {
    if (object instanceof THREE.Mesh) {
      object.geometry.dispose();
      const material = object.material;
      if (Array.isArray(material)) {
        for (const m of material) disposeMaterial(m);
      } else {
        disposeMaterial(material);
      }
    }
  });
  viewer.renderer.dispose();
  viewers.delete(assetId);
}

function disposeMaterial(material: THREE.Material): void {
  for (const value of Object.values(material)) {
    if (value instanceof THREE.Texture) value.dispose();
  }
  material.dispose();
}

export async function createModelViewer(
  assetId: string,
  url: string,
  width = VIEWER_SIZE,
  height = VIEWER_SIZE
): Promise<HTMLCanvasElement | null> {
  const existing = viewers.get(assetId);
  if (existing) return existing.canvas;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const renderer = new THREE.WebGLRenderer({
    canvas,
    alpha: true,
    antialias: true,
    preserveDrawingBuffer: true,
  });
  renderer.setSize(width, height, false);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
  camera.position.set(0, 0.8, 3);
  camera.lookAt(0, 0.6, 0);

  const group = new THREE.Group();
  scene.add(group);

  scene.add(new THREE.AmbientLight(0xffffff, 1.4));
  const key = new THREE.DirectionalLight(0xffffff, 2.2);
  key.position.set(2, 3, 2);
  scene.add(key);
  const rim = new THREE.DirectionalLight(0x8899ff, 0.8);
  rim.position.set(-2, 1, -2);
  scene.add(rim);

  const viewer: ModelViewer = { canvas, renderer, scene, camera, group, loaded: false };
  viewers.set(assetId, viewer);
  startLoop();

  try {
    const response = await fetch(url);
    const buffer = await response.arrayBuffer();
    const loader = new GLTFLoader();
    const gltf = await loader.parseAsync(buffer, url);
    const model = gltf.scene;
    fitModel(model);
    group.add(model);
    viewer.loaded = true;
  } catch (error) {
    viewers.delete(assetId);
    renderer.dispose();
    console.warn(`Failed to load 3D model ${assetId}`, error);
    return null;
  }

  return canvas;
}

function fitModel(model: THREE.Object3D): void {
  const box = new THREE.Box3().setFromObject(model);
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  const max = Math.max(size.x, size.y, size.z) || 1;
  const scale = 2.4 / max;
  model.scale.setScalar(scale);
  model.position.sub(center.clone().multiplyScalar(scale));
  model.position.y += (size.y * scale) / 2 - 0.5;
}
